import { useMemo } from 'react';

interface FilterOptions {
  searchFields?: string[];
  filterFields?: Record<string, (item: any, value: any) => boolean>;
}

export const useDataFilter = <T extends Record<string, any>>(
  data: T[],
  searchValue: string,
  filterValues: Record<string, any>,
  options?: FilterOptions
) => {
  const filteredData = useMemo(() => {
    const defaultSearchFields = options?.searchFields || ['title', 'name', 'firstName', 'lastName', 'email'];
    const filterFieldsConfig = options?.filterFields || {};

    let result = [...data];

    // Apply search filter
    if (searchValue.trim()) {
      const searchLower = searchValue.toLowerCase();
      result = result.filter((item) =>
        defaultSearchFields.some((field) => {
          const value = item[field];
          if (!value) return false;
          if (Array.isArray(value)) {
            return value.some((v: any) => v && v.toString().toLowerCase().includes(searchLower));
          }
          return value.toString().toLowerCase().includes(searchLower);
        })
      );
    }

    // Apply field-specific filters
    Object.entries(filterValues).forEach(([key, value]) => {
      if (value && value !== '') {
        if (filterFieldsConfig[key]) {
          // Use custom filter function if provided
          result = result.filter((item) => filterFieldsConfig[key](item, value));
        } else {
          // Default filter: exact match or array includes
          result = result.filter((item) => {
            const itemValue = item[key];
            if (Array.isArray(value)) {
              return value.includes(itemValue);
            }
            return itemValue === value || itemValue?.toString().toLowerCase() === value.toString().toLowerCase();
          });
        }
      }
    });

    return result;
  }, [data, searchValue, filterValues, options]);

  return filteredData;
};
